/**
 * Yault credential NFT — server-side mint flow.
 *
 * Encrypts credential payload locally with recipient's xidentity, then POSTs
 * to upload-and-mint API. No registry, signer, or client-side mint needed.
 */

import { AssetType } from './types.js';
import { ECIESEncryptor } from './encryption.js';
import { encryptAsset } from './encryption.js';

/** Payload shape agreed with Yault webapp */
export interface CredentialNftPayload {
  user_cred: string;
  mnemonic?: string | null;
  index: number;
  label: string;
  /** Optional memo (e.g. "letter to future heirs") — encrypted together with credential, delivered when release triggers. */
  memo?: string | null;
}

/** Result shape expected by Yault */
export interface CredentialMintResult {
  success: boolean;
  txId?: string;
  error?: string;
}

/** Options for server-side mint */
export interface MintCredentialNftViaServerOptions {
  /** Recipient's xidentity (base64). Required. */
  xidentity: string;
  /** Upload-and-mint API URL. If omitted, uses built-in prod/dev endpoint. */
  uploadAndMintApiUrl?: string;
  /** When true, uses built-in dev endpoint (if uploadAndMintApiUrl not set). */
  dev?: boolean;
  /** Optional: custom headers (e.g. Authorization) for the API request */
  headers?: Record<string, string>;
  /** Network for mint (mainnet | devnet). Default: mainnet */
  network?: 'mainnet' | 'devnet';
}

/** Options for preparing payload only (no POST). Same as MintCredentialNftViaServerOptions minus upload/headers. */
export interface PrepareCredentialNftPayloadOptions {
  /** Recipient's xidentity (base64). Required. */
  xidentity: string;
  /** Network for mint (mainnet | devnet). Default: mainnet */
  network?: 'mainnet' | 'devnet';
}

/** Result of prepareCredentialNftPayload: the exact body to POST to upload-and-mint later. */
export interface PreparedCredentialNftPayload {
  /** Request body to POST to upload-and-mint API (data, contentType, tags, leafOwner, name, description, network). */
  body: Record<string, unknown>;
  /** Recipient Solana address (leafOwner). */
  recipientSolanaAddress: string;
}

/** Built-in endpoints for upload-and-mint API */
export const RWA_UPLOAD_AND_MINT_ENDPOINTS = {
  prod: 'https://api.yallet.xyz/api/v1/storage/rwa/upload-and-mint',
  dev: 'https://api.yallet.xyz/api/v1/storage/rwa/upload-and-mint',
} as const;

const NOTE_TITLE = 'Yault Release Credentials';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Build the same upload-and-mint request body as mintCredentialNftViaServer, without sending.
 * Used to store the encrypted note server-side (e.g. Oracle binding) and send only after attestation.
 *
 * @param recipientSolanaAddress - Recipient's Solana address (leafOwner)
 * @param payload - { user_cred, mnemonic?, index, label }
 * @param options - { xidentity, network? }
 * @returns { body, recipientSolanaAddress } — body is the exact JSON to POST to upload-and-mint later
 */
export async function prepareCredentialNftPayload(
  recipientSolanaAddress: string,
  payload: CredentialNftPayload,
  options: PrepareCredentialNftPayloadOptions
): Promise<PreparedCredentialNftPayload> {
  const { xidentity, network = 'mainnet' } = options;

  if (!xidentity || !xidentity.trim()) {
    throw new Error('xidentity is required');
  }

  const note = {
    title: NOTE_TITLE,
    content: JSON.stringify(payload),
    metadata: { createdAt: new Date().toISOString() },
  };

  const name = `${NOTE_TITLE} — ${payload.label}`;
  const description = `Path index ${payload.index}. Decrypt in Yallet to view credentials.`;

  const encryptor = new ECIESEncryptor();
  const encrypted = await encryptAsset(note, AssetType.NOTE, xidentity.trim(), encryptor);

  const payloadForStorage = {
    encrypted: {
      ephemeral_pub: encrypted.ephemeral_pub,
      encrypted_aes_key: encrypted.encrypted_aes_key,
      iv: encrypted.iv,
      encrypted_data: encrypted.encrypted_data,
    },
    metadata: {
      assetType: AssetType.NOTE,
      direction: 'received' as const,
      network,
      version: encrypted.version,
      uuid: encrypted.uuid,
      timestamp: encrypted.timestamp,
      dataSize: encrypted.dataSize,
      uploadedAt: Date.now(),
    },
  };

  const jsonString = JSON.stringify(payloadForStorage);
  const dataBase64 = bytesToBase64(new TextEncoder().encode(jsonString));

  const body: Record<string, unknown> = {
    data: dataBase64,
    contentType: 'application/json',
    tags: { 'Yallet-Type': 'encrypted-asset', 'Asset-Type': AssetType.NOTE, 'Direction': 'received' },
    leafOwner: recipientSolanaAddress,
    name,
    description,
    network,
  };

  return { body, recipientSolanaAddress };
}

/**
 * Mint credential payload via server (upload-and-mint API).
 *
 * Caller provides xidentity from recipient-addresses. No registry or signer.
 *
 * @param recipientSolanaAddress - Recipient's Solana address (leafOwner)
 * @param payload - { user_cred, mnemonic?, index, label }
 * @param options - { xidentity, uploadAndMintApiUrl?, dev?, headers?, network? }
 * @returns { success, txId?, error? }
 */
export async function mintCredentialNftViaServer(
  recipientSolanaAddress: string,
  payload: CredentialNftPayload,
  options: MintCredentialNftViaServerOptions
): Promise<CredentialMintResult> {
  const { xidentity, uploadAndMintApiUrl, dev = false, headers = {}, network = 'mainnet' } = options;

  if (!xidentity || !xidentity.trim()) {
    return { success: false, error: 'xidentity is required' };
  }

  const apiUrl =
    uploadAndMintApiUrl ||
    (dev ? RWA_UPLOAD_AND_MINT_ENDPOINTS.dev : RWA_UPLOAD_AND_MINT_ENDPOINTS.prod);

  const { body } = await prepareCredentialNftPayload(recipientSolanaAddress, payload, { xidentity, network });

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(body),
    });

    const result = (await response.json().catch(() => ({}))) as {
      success?: boolean;
      mint?: { signature?: string };
      error?: string;
    };

    if (!response.ok) {
      return {
        success: false,
        error: result?.error || response.statusText || 'Upload-and-mint failed',
      };
    }

    const signature = result?.mint?.signature;
    return {
      success: true,
      txId: signature,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { success: false, error: message };
  }
}
